import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { IOnboardingStatus, IOnboardingSteps } from '../../interfaces/onboarding.interface';
import { OnboardingService } from '../../services/onboarding.service';

interface IChecklistStep {
    key: keyof IOnboardingSteps;
    /** Translation key prefix: `<prefix>.title` and `<prefix>.hint`. */
    label: string;
    route: string[];
}

/** In the order a new coach is meant to work through them. */
const STEPS: IChecklistStep[] = [
    { key: 'sessionCreated', label: 'onboarding.steps.session', route: ['/', ROUTE_CONSTANTS.HOME] },
    { key: 'playerAdded', label: 'onboarding.steps.player', route: ['/', ROUTE_CONSTANTS.SQUAD] },
    { key: 'historyViewed', label: 'onboarding.steps.history', route: ['/', ROUTE_CONSTANTS.SESSIONS] },
    { key: 'calendarEventCreated', label: 'onboarding.steps.calendar', route: ['/', ROUTE_CONSTANTS.CALENDAR] },
];

/** Remembers on this device whether the coach folded the checklist away. */
const COLLAPSED_STORAGE_KEY = 'saideline.onboarding.collapsed';

/**
 * Getting-started checklist in the bottom-right corner for signed-in coaches. Ticks steps off
 * from the coach's own data and stays until they close it.
 */
@Component({
    selector: 'app-onboarding-checklist',
    templateUrl: './onboarding-checklist.component.html',
    styleUrl: './onboarding-checklist.component.scss',
    standalone: false,
})
export class OnboardingChecklistComponent {
    readonly steps = STEPS;
    status: IOnboardingStatus | null = null;
    isCollapsed: boolean;

    private isSignedIn = false;
    /** On the start page the checklist always opens folded, so the training form's RUN button stays visible. */
    private isOnHome: boolean;

    constructor(
        private readonly onboardingService: OnboardingService,
        authService: AuthApiService,
        router: Router
    ) {
        const destroyRef = inject(DestroyRef);
        this.isOnHome = this.isHomeUrl(router, router.url);
        this.isCollapsed = this.isOnHome || this.readCollapsed();

        this.onboardingService.status$.pipe(takeUntilDestroyed(destroyRef)).subscribe((status) => {
            this.status = status;
        });

        authService.user$.pipe(takeUntilDestroyed(destroyRef)).subscribe((user) => {
            this.isSignedIn = !!user;
            if (user) {
                this.onboardingService.refresh();
            } else {
                this.onboardingService.clear();
            }
        });

        // Most steps finish on another page (a generated session opens the viewer), so re-check on navigation.
        router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(destroyRef)
            )
            .subscribe((event) => {
                this.isOnHome = this.isHomeUrl(router, event.urlAfterRedirects);
                this.isCollapsed = this.isOnHome || this.readCollapsed();

                if (this.isSignedIn && this.status && !this.status.dismissed && !this.isComplete) {
                    this.onboardingService.refresh();
                }
            });
    }

    get isVisible(): boolean {
        return this.isSignedIn && !!this.status && !this.status.dismissed;
    }

    get doneCount(): number {
        return this.steps.filter((step) => this.isDone(step)).length;
    }

    get isComplete(): boolean {
        return this.doneCount === this.steps.length;
    }

    /** Share of finished steps, 0–100, for the progress bar. */
    get progress(): number {
        return Math.round((this.doneCount / this.steps.length) * 100);
    }

    isDone(step: IChecklistStep): boolean {
        return !!this.status?.steps[step.key];
    }

    toggleCollapsed(): void {
        this.isCollapsed = !this.isCollapsed;
        if (this.isOnHome) {
            // Opening it on the start page is a one-off; the next visit starts folded again.
            return;
        }
        try {
            localStorage.setItem(COLLAPSED_STORAGE_KEY, String(this.isCollapsed));
        } catch {
            // Storage blocked: the choice just isn't remembered.
        }
    }

    dismiss(): void {
        this.onboardingService.dismiss();
    }

    /** The start page (training form), with or without query params such as `?date=`. */
    private isHomeUrl(router: Router, url: string): boolean {
        return !router.parseUrl(url).root.children['primary']?.segments.length;
    }

    /** Folded by default on phones so the checklist doesn't cover the page. */
    private readCollapsed(): boolean {
        try {
            const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
            if (stored !== null) {
                return stored === 'true';
            }
        } catch {
            // Storage blocked: fall back to the screen-size default.
        }
        return typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches === true;
    }
}
